import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Alert } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../../../src/modules/home/ui/Auth.css";
import portnetLogo from "../../assets/images/portnetLogo.png";
import config from "../../common/Config";
import { validateMotDePasse } from "../utils/validateUtils";

const FEATURE_BADGES = [
  { icon: ShieldOutlinedIcon, label: "Connexion sécurisée" },
  { icon: AccessTimeIcon, label: "Disponibilité 24/24" },
  { icon: SpeedOutlinedIcon, label: "Performance et traçabilité" },
];

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { enqueueSnackbar } = useSnackbar();

  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async () => {
    setErrorMessage("");

    if (!token) {
      setErrorMessage("Lien de réinitialisation invalide ou expiré.");
      return;
    }

    const { errors: validationErrors } = validateMotDePasse({
      newPassword,
      confirmNewPassword,
    });
    const fieldErrors = {
      newPassword: validationErrors.newPassword,
      confirmNewPassword: validationErrors.confirmNewPassword,
    };
    setErrors(fieldErrors);
    if (fieldErrors.newPassword || fieldErrors.confirmNewPassword) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${config.crcUrl}/api/user/resetPassword/confirm`,
        { token, newPassword, confirmNewPassword },
      );
      enqueueSnackbar(
        response?.data?.message || "Mot de passe mis à jour avec succès.",
        { variant: "success" },
      );
      navigate("/");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          "Lien de réinitialisation invalide ou expiré.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Mise à jour du mot de passe - Portail Banques</title>
      </Helmet>

      <div className="auth-container">
        <aside className="auth-left-panel">
          <div className="auth-pre-align">
            <div className="auth-logo-container">
              <img src={portnetLogo} alt="PORTNET" className="auth-logo" />
            </div>
          </div>
          <div className="auth-align-block">
            <h1 className="auth-headline auth-headline--portail">
              Portail{" "}
              <span className="auth-headline-underline">
                <b>Banques</b>
              </span>
            </h1>

            <div className="auth-badges">
              {FEATURE_BADGES.map(({ icon: Icon, label }) => (
                <span key={label} className="auth-badge">
                  <Icon fontSize="small" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </aside>

        <div className="auth-right-column">
          <main className="auth-right-panel">
            <div className="auth-form-wrapper">
              <div className="auth-pre-align">
                <div className="auth-section-label auth-section-label--red">
                  Réinitialisation
                </div>
                <h2 className="auth-form-title">Mise à jour du mot de passe</h2>
                <p className="auth-form-subtitle">
                  Choisissez un nouveau mot de passe pour votre compte.
                </p>
                {errorMessage && (
                  <Alert className="auth-alert" severity="error">
                    {errorMessage}
                  </Alert>
                )}
              </div>

              <div className="auth-align-block">
                <br />
                <div className="auth-field-group">
                  <label
                    className="auth-field-label"
                    htmlFor="reset-new-password"
                  >
                    Nouveau mot de passe
                  </label>
                  <div className="auth-password-wrapper">
                    <input
                      id="reset-new-password"
                      className="auth-field-input auth-field-input--password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onKeyDown={handleKeyPress}
                      autoComplete="new-password"
                    />
                    {errors.newPassword && (
                      <span className="auth-field-error">
                        {errors.newPassword}
                      </span>
                    )}
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowNewPassword((p) => !p)}
                      aria-label="Afficher ou masquer le mot de passe"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </button>
                  </div>
                </div>

                <div className="auth-field-group">
                  <label
                    className="auth-field-label"
                    htmlFor="reset-confirm-password"
                  >
                    Confirmer le mot de passe
                  </label>
                  <div className="auth-password-wrapper">
                    <input
                      id="reset-confirm-password"
                      className="auth-field-input auth-field-input--password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      onKeyDown={handleKeyPress}
                      autoComplete="new-password"
                    />
                    {errors.confirmNewPassword && (
                      <span className="auth-field-error">
                        {errors.confirmNewPassword}
                      </span>
                    )}
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowConfirmPassword((p) => !p)}
                      aria-label="Afficher ou masquer le mot de passe"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="auth-submit-btn"
                  style={{ marginTop: "1.5em" }}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  Soumettre
                  <ArrowForwardIcon fontSize="small" />
                </button>
              </div>
            </div>
          </main>

          <footer className="auth-footer">
            <span>© 2026 PORTNET S.A.</span>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
